import { useMemo } from 'react';
import {
  useOmnichannelAccounts,
  useOmnichannelConversations,
  useOmnichannelStatus,
} from '@/api';
import { transformConversation } from '@/pages/dashboard/conversations/utils/transformers';
import type {
  ChannelType,
  Conversation,
  OmnichannelAccount,
  OmnichannelConversation,
} from '@/types';

type ChannelStatus = boolean | { pageId?: string };

const getChannelStatus = (
  status: ChannelStatus | undefined
): { connected: boolean; pageId?: string } => {
  if (!status) return { connected: false };
  if (typeof status === 'boolean') return { connected: status };
  return { connected: !!status.pageId, pageId: status.pageId };
};

const getPageIdFromAccounts = (
  accounts: OmnichannelAccount[] | undefined,
  channel: ChannelType,
  fallbackAccounts?: OmnichannelAccount[]
): string | undefined => {
  if (!accounts || accounts.length === 0) {
    if (channel === 'instagram' && fallbackAccounts) {
      const accountWithInstagram = fallbackAccounts.find(
        (acc) => acc.instagram_business_account
      );
      return accountWithInstagram?.instagram_business_account?.id;
    }
    return undefined;
  }

  if (channel === 'instagram') {
    const accountWithInstagram = accounts.find(
      (acc) => acc.instagram_business_account
    );
    return (
      accountWithInstagram?.instagram_business_account?.id ||
      accounts[0]?.pageId
    );
  }

  return accounts[0]?.pageId;
};

const toConversationList = (
  arr: OmnichannelConversation[] | undefined,
  channel: ChannelType,
  pageId?: string
): Conversation[] =>
  (arr ?? [])
    .filter((conv) => conv != null && typeof conv === 'object')
    .map((conv) => transformConversation(conv, channel, pageId));

export function useOmnichannelConversationLists() {
  const { data: status, isLoading: isLoadingStatus } = useOmnichannelStatus();

  const channelStatuses = useMemo(() => {
    if (!status) {
      return {
        facebook: { connected: false },
        instagram: { connected: false },
        whatsapp: { connected: false },
      };
    }
    return {
      facebook: getChannelStatus(status.facebook),
      instagram: getChannelStatus(status.instagram),
      whatsapp: getChannelStatus(status.whatsapp),
    };
  }, [status]);

  const { data: facebookAccounts } = useOmnichannelAccounts({
    channel: 'facebook',
    enabled: channelStatuses.facebook.connected,
  });
  const { data: instagramAccounts } = useOmnichannelAccounts({
    channel: 'instagram',
    enabled: channelStatuses.instagram.connected,
  });
  const { data: whatsappAccounts } = useOmnichannelAccounts({
    channel: 'whatsapp',
    enabled: channelStatuses.whatsapp.connected,
  });

  const facebookPageId = useMemo(
    () => getPageIdFromAccounts(facebookAccounts, 'facebook'),
    [facebookAccounts]
  );
  const instagramPageId = useMemo(
    () =>
      getPageIdFromAccounts(instagramAccounts, 'instagram', facebookAccounts),
    [instagramAccounts, facebookAccounts]
  );
  const whatsappPageId = useMemo(
    () => getPageIdFromAccounts(whatsappAccounts, 'whatsapp'),
    [whatsappAccounts]
  );

  const {
    data: facebookConversationsData,
    isLoading: isLoadingFacebookConversations,
  } = useOmnichannelConversations({
    channel: 'facebook',
    pageId: facebookPageId,
    enabled: channelStatuses.facebook.connected && !!facebookPageId,
  });

  const {
    data: instagramConversationsData,
    isLoading: isLoadingInstagramConversations,
  } = useOmnichannelConversations({
    channel: 'instagram',
    pageId: instagramPageId,
    enabled: channelStatuses.instagram.connected && !!instagramPageId,
  });

  const {
    data: whatsappConversationsData,
    isLoading: isLoadingWhatsappConversations,
  } = useOmnichannelConversations({
    channel: 'whatsapp',
    pageId: whatsappPageId,
    enabled: channelStatuses.whatsapp.connected && !!whatsappPageId,
  });

  const conversationsByChannel = useMemo(
    () => ({
      facebook: toConversationList(
        facebookConversationsData?.data,
        'facebook',
        facebookPageId
      ),
      instagram: toConversationList(
        instagramConversationsData?.data,
        'instagram',
        instagramPageId
      ),
      whatsapp: toConversationList(
        whatsappConversationsData?.data,
        'whatsapp',
        whatsappPageId
      ),
    }),
    [
      facebookConversationsData,
      instagramConversationsData,
      whatsappConversationsData,
      facebookPageId,
      instagramPageId,
      whatsappPageId,
    ]
  );

  const allConversations = useMemo(
    () =>
      [
        ...conversationsByChannel.facebook,
        ...conversationsByChannel.instagram,
        ...conversationsByChannel.whatsapp,
      ].sort((a, b) => {
        const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return tb - ta;
      }),
    [conversationsByChannel]
  );

  const hasAnyAccountConnected =
    channelStatuses.facebook.connected ||
    channelStatuses.instagram.connected ||
    channelStatuses.whatsapp.connected;

  const isLoading =
    isLoadingStatus ||
    isLoadingFacebookConversations ||
    isLoadingInstagramConversations ||
    isLoadingWhatsappConversations;

  return {
    conversationsByChannel,
    allConversations,
    hasAnyAccountConnected,
    isLoading,
  };
}
